import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useResize } from '@daily/shared/hooks/useResize';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import deepEqual from 'fast-deep-equal';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallState } from '../../contexts/CallProvider';
import { useCallConfig } from '../../hooks/useCallConfig';
import { useCustomIntegrations } from '../../hooks/useCustomIntegrations';
import {
  useCustomTrayButtons,
  useShowLeaveButton,
} from '../../lib/state/custom';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { AppsControls } from './AppsControls';
import { AudioControls } from './AudioControls';
import { BreakoutControls } from './BreakoutControls';
import { ChatControls } from './ChatControls';
import { ContactHostControls } from './ContactHostControls';
import { CustomButton } from './CustomButton';
import { EmojiReactionControls } from './EmojiReactionControls';
import { LeaveButton } from './LeaveButton';
import { MoreControls } from './MoreControls';
import { NetworkControls } from './NetworkControls';
import { PeopleControls } from './PeopleControls';
import { RaiseHandControls } from './RaiseHandControls';
import { RecordControls } from './RecordControls';
import { ScreenShareControls } from './ScreenShareControls';
import { VideoControls } from './VideoControls';

export type Control =
  | 'people'
  | 'chat'
  | 'screenshare'
  | 'record'
  | 'network'
  | 'raisehand'
  | 'emoji-reaction'
  | 'breakouts'
  | 'apps'
  | 'contact-host'
  | string;

type ControlWidths = Partial<{
  [K in Control]: number;
}>;

const MORE_ID = 'tray-more-btn';
const LEAVE_ID = 'btn-leave';

export const Tray: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [customTrayButtons] = useCustomTrayButtons();
  const { disableAudio } = useCallState();
  const [showLeaveButton] = useShowLeaveButton();
  const {
    broadcastRole,
    enableBreakoutRooms,
    enableChat,
    enableEmojiReactions,
    enableHandRaising,
    enableNetworkUI,
    enablePeopleUI,
    enableRecording,
    enableRecordingUI,
    enableScreenShare,
  } = useCallConfig();
  const { canControlIntegration, mainIntegrations } = useCustomIntegrations();
  const { breakoutHost, breakoutStatus } = useBreakoutSessionState();

  const localSessionId = useLocalSessionId();
  const isLocalOwner = useParticipantProperty(localSessionId, 'owner');

  const isAttendee = useMemo(
    () => broadcastRole === 'attendee',
    [broadcastRole]
  );

  const apps = mainIntegrations.filter(canControlIntegration);

  const controlsRef = useRef<HTMLDivElement>(null);
  const secondaryRef = useRef<HTMLDivElement>(null);
  const defaultControls = useMemo(() => {
    const controls: Control[] = [];
    if (enablePeopleUI) controls.push('people');
    if (enableChat) controls.push('chat');
    if (enableEmojiReactions) controls.push('emoji-reaction');
    /* In broadcast only calls, we want to enable raise hand controls for participants only*/
    if (!isAttendee && enableHandRaising) controls.push('raisehand');
    if (enableScreenShare) controls.push('screenshare');
    if (enableRecording && enableRecordingUI) controls.push('record');
    if (enableNetworkUI) controls.push('network');
    if (isLocalOwner && enableBreakoutRooms) controls.push('breakouts');
    if (apps.length) controls.push('apps');
    if (breakoutStatus === 'ongoing' && breakoutHost !== localSessionId)
      controls.push('contact-host');
    if (customTrayButtons) controls.push(...Object.keys(customTrayButtons));
    return controls;
  }, [
    apps.length,
    breakoutHost,
    breakoutStatus,
    customTrayButtons,
    enableBreakoutRooms,
    enableChat,
    enableEmojiReactions,
    enableHandRaising,
    enableNetworkUI,
    enablePeopleUI,
    enableRecording,
    enableRecordingUI,
    enableScreenShare,
    isAttendee,
    isLocalOwner,
    localSessionId,
  ]);
  const [visibleControls, setVisibleControls] =
    useState<Control[]>(defaultControls);
  const [menuControls, setMenuControls] = useState<Control[]>([]);

  useResize(
    useCallback(() => {
      if (!controlsRef.current) return;

      document.body.style.setProperty(
        '--tray-height',
        `${controlsRef.current.closest('.tray').clientHeight}px`
      );

      const { clientWidth } = controlsRef.current;

      const controlWidths: ControlWidths = defaultControls.reduce(
        (widths, control) => {
          widths[control] = document.getElementById(
            `${control}-controls`
          )?.clientWidth;
          return widths;
        },
        {}
      );

      const secondaryWidth = secondaryRef.current.clientWidth;
      const moreWidth = document.getElementById(MORE_ID)?.clientWidth ?? 0;
      const leaveWidth = document.getElementById(LEAVE_ID)?.clientWidth ?? 0;
      const availableSecondaryWidth = secondaryWidth - moreWidth - leaveWidth;
      let availableWidth = clientWidth + availableSecondaryWidth;
      const visControls = [];
      const menControls = [];
      for (const [control, width] of Object.entries(controlWidths)) {
        if (!defaultControls.includes(control)) continue;
        if (availableWidth >= width) {
          availableWidth -= width;
          visControls.push(control);
        } else {
          availableWidth = 0;
          menControls.push(control);
        }
      }

      // Don't update visibleControls, if nothing has changed.
      // Avoids infinite state update loop.
      if (deepEqual(visibleControls, visControls)) return;

      setMenuControls(menControls);
      setVisibleControls(visControls);
    }, [defaultControls, visibleControls])
  );

  return (
    <div className="tray">
      {!isAttendee ? (
        <div className="av-controls">
          <VideoControls id="video-controls" />
          {!disableAudio && <AudioControls id="audio-controls" />}
        </div>
      ) : (
        <Text variant="strong" style={{ paddingLeft: 8, flex: 1 }}>
          {t('broadcast.viewingCall')}
        </Text>
      )}

      <div ref={controlsRef} className="controls">
        <PeopleControls
          id="people-controls"
          visible={visibleControls.includes('people')}
        />
        <ChatControls
          id="chat-controls"
          visible={visibleControls.includes('chat')}
        />
        <EmojiReactionControls
          id="emoji-reaction-controls"
          visible={visibleControls.includes('emoji-reaction')}
        />
        <RaiseHandControls
          id="raisehand-controls"
          visible={visibleControls.includes('raisehand')}
        />
        <ScreenShareControls
          id="screenshare-controls"
          visible={visibleControls.includes('screenshare')}
        />
        <RecordControls
          id="record-controls"
          visible={visibleControls.includes('record')}
        />
        <NetworkControls
          id="network-controls"
          visible={visibleControls.includes('network')}
        />
        <BreakoutControls
          id="breakouts-controls"
          visible={visibleControls.includes('breakouts')}
        />
        <AppsControls
          id="apps-controls"
          visible={visibleControls.includes('apps')}
        />
        {Object.entries(customTrayButtons ?? {}).map(([id, btn]) => (
          <CustomButton
            btn={btn}
            id={id}
            key={id}
            visible={visibleControls.includes(id)}
          />
        ))}
      </div>
      <div ref={secondaryRef} className="secondary-controls">
        <ContactHostControls
          id="contact-host-controls"
          visible={visibleControls.includes('contact-host')}
        />
        <MoreControls controls={menuControls} id={MORE_ID} />
        {showLeaveButton && <LeaveButton id={LEAVE_ID} />}
      </div>
      <style jsx>{`
        .tray {
          align-items: center;
          background: var(--card-bg);
          border-top: 1px solid var(--card-border);
          bottom: 0;
          display: flex;
          justify-content: space-between;
          left: 0;
          padding: 8px 4px;
          position: fixed;
          width: 100vw;
          z-index: var(--zindex-tray);
        }
        .av-controls,
        .secondary-controls {
          align-items: center;
          display: flex;
          flex: 1;
        }
        .secondary-controls {
          justify-content: flex-end;
        }
        .tray .controls {
          align-items: center;
          display: flex;
          flex: 1 1 auto;
          flex-direction: row;
          justify-content: center;
          min-width: 0;
        }
        // TODO: Remove (PRE-606)
        .tray :global(.integration.active) {
          background: ${colors.backgroundAccent};
        }
      `}</style>
    </div>
  );
};
